import { type JSX, useEffect, useRef } from "react";

export type ToastKind = "success" | "error" | "warning" | "info";

export interface ToastMessage {
  readonly kind: ToastKind;
  readonly message: string;
}

/** Default auto-dismiss delay. Kept here (not at each call site) so every
 * consumer gets the same self-closing behavior for free. */
const DEFAULT_DURATION_MS = 4000;

const KIND_ACCENT_COLOR: Record<ToastKind, string> = {
  success: "var(--toast-accent-success)",
  error: "var(--toast-accent-error)",
  warning: "var(--toast-accent-warning)",
  info: "var(--toast-accent-info)",
};

/** Floating, self-dismissing notification pinned to the top-right of the
 * viewport. Auto-dismiss is intrinsic to the component: the timer lives here,
 * so callers only manage the message state and never wire their own timeout. */
export function Toast({
  feedback,
  onDismiss,
  duration = DEFAULT_DURATION_MS,
}: {
  feedback: ToastMessage;
  onDismiss: () => void;
  duration?: number;
}): JSX.Element {
  // Hold the latest `onDismiss` in a ref so an inline arrow at the call site
  // (a fresh identity every render) doesn't restart the timer on each parent
  // re-render — the countdown only resets when the message or duration
  // actually changes.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `feedback` is intentional — it isn't read in the effect body, but depending on its identity restarts the countdown for every new toast (callers store it in state, so the reference is stable between renders and only changes when a new toast is shown — including one with identical text).
  useEffect(() => {
    const id = window.setTimeout(() => onDismissRef.current(), duration);
    return () => window.clearTimeout(id);
  }, [feedback, duration]);

  return (
    <div
      role={feedback.kind === "error" ? "alert" : "status"}
      className="fixed right-4 top-4 z-[100] w-[min(calc(100vw-2rem),28rem)] rounded-md border border-l-4 bg-card p-3 text-body-2 text-card-foreground shadow-lg"
      data-toast-kind={feedback.kind}
      style={{ borderLeftColor: KIND_ACCENT_COLOR[feedback.kind] }}
    >
      <div className="flex items-start justify-between gap-3">
        <span>{feedback.message}</span>
        <button
          type="button"
          onClick={onDismiss}
          // A toast can sit above an active modal. Pointer activation must not
          // move focus outside that modal just before this button unmounts.
          onPointerDown={(event) => event.preventDefault()}
          aria-label="cerrar notificación"
          className="text-body-3 underline hover:no-underline"
        >
          cerrar
        </button>
      </div>
    </div>
  );
}
